import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { shopify } from "@/lib/api";
import { formatPrice } from "@/lib/store";
import { t } from "@/lib/i18n";
import { optimizeImg, srcSet } from "@/lib/img";

/**
 * "Fullfør stilen" — editorial cross-sell strip at the bottom of the PDP.
 * Pulls 3 complementary products from the backend (hoodie <-> cap, color-matched
 * first). Sold-out siblings are shown with an UTSOLGT overlay.
 */
export default function CompleteTheLook({ handle }) {
  const { data: items = [] } = useQuery({
    queryKey: ["complete-the-look", handle],
    queryFn: () => shopify.completeTheLook(handle),
    enabled: !!handle,
    staleTime: 60_000,
  });

  if (items.length === 0) return null;

  return (
    <section
      className="border-t border-[color:var(--marcini-border)] px-5 md:px-12 py-14 md:py-24 mt-12 md:mt-20"
      data-testid="complete-the-look"
    >
      <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="text-eyebrow mb-3">{t.pdp.completeTheLookEyebrow}</p>
          <h2 className="font-serif-editorial text-4xl md:text-6xl leading-[0.9]">
            {t.pdp.completeTheLookTitle}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-10">
        {items.map((item) => (
          <Link
            key={item.handle}
            to={`/products/${item.handle}`}
            data-testid={`ctl-${item.handle}`}
            className="group block"
          >
            <div className="relative overflow-hidden bg-[color:var(--marcini-bg-warm)] aspect-[3/4]">
              <img
                src={optimizeImg(item.thumbnail, 800)}
                srcSet={srcSet(item.thumbnail, [400, 800, 1200])}
                sizes="(min-width: 768px) 33vw, 50vw"
                alt={item.color_name || item.title}
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                loading="lazy"
              />
              {!item.available && (
                <span className="absolute top-3 left-3 bg-white text-black text-[11px] tracking-[0.28em] font-medium px-3 py-1.5 border-l-2 border-[#c8102e] shadow-sm">
                  {t.common.soldOut.toUpperCase()}
                </span>
              )}
            </div>
            <div className="mt-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-serif-editorial text-lg md:text-xl leading-tight">
                  {item.title}
                </p>
                {item.color_name && item.color_name !== item.title && (
                  <p className="text-eyebrow text-[color:var(--marcini-fg-3)] mt-1 truncate">
                    {item.color_name}
                  </p>
                )}
              </div>
              <p className="text-sm mt-1 whitespace-nowrap">{formatPrice(item.price)}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
